import {FC} from 'react'

interface Props {
  page: number
  limit: number
  total: number
  onPageChange: (page: number) => void
}

// Compact page window: first, last, current ±1, with … gaps.
function pageWindow(current: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) return Array.from({length: totalPages}, (_, i) => i + 1)
  const out: (number | '…')[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(totalPages - 1, current + 1)
  if (start > 2) out.push('…')
  for (let p = start; p <= end; p++) out.push(p)
  if (end < totalPages - 1) out.push('…')
  out.push(totalPages)
  return out
}

const Pagination: FC<Props> = ({page, limit, total, onPageChange}) => {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  if (total === 0) return null

  const from = (page - 1) * limit + 1
  const to = Math.min(page * limit, total)

  return (
    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 pt-4'>
      <span className='text-muted fs-7'>
        Showing <b>{from}</b>–<b>{to}</b> of <b>{total}</b>
      </span>
      <ul className='pagination m-0'>
        <li className={`page-item previous ${page <= 1 ? 'disabled' : ''}`}>
          <button className='page-link' onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
            <i className='previous' />
          </button>
        </li>
        {pageWindow(page, totalPages).map((p, i) =>
          p === '…' ? (
            <li key={`gap-${i}`} className='page-item disabled'>
              <span className='page-link'>…</span>
            </li>
          ) : (
            <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
              <button className='page-link' onClick={() => onPageChange(p)}>
                {p}
              </button>
            </li>
          )
        )}
        <li className={`page-item next ${page >= totalPages ? 'disabled' : ''}`}>
          <button className='page-link' onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
            <i className='next' />
          </button>
        </li>
      </ul>
    </div>
  )
}

export {Pagination}
